/**
 * @file ConversationListToolbar.tsx
 * @description Шапка списка диалогов (chat-window-design.md §1б–д, §7):
 * вкладки «Все / Мои / Архив» со счётчиками, отбор «Без ответственного»,
 * отбор «Клиент: … ×», поиск по имени клиента и номеру заказа.
 *
 * Логист видит один список «Мои» без вкладок и без отбора (допущение
 * дизайнера по R4.5/R4.7): ему не из чего выбирать.
 *
 * Числа на вкладках и отборе — диалоги с моим непрочитанным (`counts.unread`,
 * R4.19), как на кнопке чата; ноль не показываем.
 */

import { X } from "lucide-react";

import { FilterToggleChips } from "../ui/FilterChips";
import { SearchInput } from "../ui/SearchInput";
import { Tabs } from "../ui/Tabs";
import type { ChatTab, ChatTabCounts } from "../../lib/api-chat";
import { CHAT_TEXT } from "./chat-texts";
import type { ChatCustomerFilter } from "./chat-window-state";

interface Props {
  canDispatch: boolean;
  tab: ChatTab;
  unassigned: boolean;
  customer: ChatCustomerFilter | null;
  search: string;
  counts: ChatTabCounts | null;
  onTab: (tab: ChatTab) => void;
  onUnassigned: (value: boolean) => void;
  onCustomer: (customer: ChatCustomerFilter | null) => void;
  onSearch: (value: string) => void;
}

/** Число непрочитанных на вкладке: ноль и «ещё не загружено» — без числа. */
function unreadBadge(n: number | undefined): number | undefined {
  return n && n > 0 ? n : undefined;
}

export function ConversationListToolbar(props: Props) {
  const { canDispatch, tab, unassigned, customer, search, counts } = props;
  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-outline-variant pb-2">
      {canDispatch ? (
        <Tabs<ChatTab>
          aria-label={CHAT_TEXT.tabsLabel}
          className="px-2"
          value={tab}
          onChange={props.onTab}
          items={[
            { value: "all", label: CHAT_TEXT.tabAll, count: unreadBadge(counts?.unread.all) },
            { value: "mine", label: CHAT_TEXT.tabMine, count: unreadBadge(counts?.unread.mine) },
            { value: "archive", label: CHAT_TEXT.tabArchive },
          ]}
        />
      ) : null}
      <div className="flex flex-col gap-2 px-3">
        <SearchInput
          value={search}
          onChange={props.onSearch}
          placeholder={CHAT_TEXT.searchPlaceholder}
          ariaLabel={CHAT_TEXT.searchLabel}
        />
        {(canDispatch && tab === "all") || customer ? (
          <div className="flex flex-wrap items-center gap-2">
            {canDispatch && tab === "all" ? (
              <FilterToggleChips
                aria-label={CHAT_TEXT.unassigned}
                items={[
                  {
                    key: "unassigned",
                    label: CHAT_TEXT.unassigned,
                    count: unreadBadge(counts?.unread.unassigned),
                    pressed: unassigned,
                    onToggle: () => props.onUnassigned(!unassigned),
                  },
                ]}
              />
            ) : null}
            {customer ? (
              <span className="inline-flex min-h-10 max-w-full items-center gap-1 rounded-full bg-accent pl-3 text-sm text-accent-foreground">
                <span className="truncate">{CHAT_TEXT.customerFilter(customer.name)}</span>
                <button
                  type="button"
                  aria-label={CHAT_TEXT.customerFilterReset}
                  onClick={() => props.onCustomer(null)}
                  className="inline-flex size-10 shrink-0 items-center justify-center rounded-full outline-none hover:bg-accent/80 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
